/**
 * Defect Overview Component
 * Migrated from web-app-legacy/js/app/Components/Defects/Overview/
 */

import { Component, OnInit, OnDestroy, Output, EventEmitter, ChangeDetectorRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { forkJoin, Subscription } from 'rxjs';

import { TranslatePipe } from '../../pipes/translate.pipe';
import { TranslationService } from '../../services/translation.service';
import { DefectService } from '../defect.service';
import { CodebookService } from '../codebook.service';
import { DefectUserRoleService } from '../defect-user-role.service';
import { SiteService } from '../../site-selector/site.service';
import { IDefect, IEmployee, DefectStateEnum } from '../defect.models';
import {
  DefectFilters,
  DefectItemViewModel,
  FiltersViewModel,
  SortDropdownOption,
  SortOption,
  SortOrder,
  UserRole,
  createDefaultFilters,
  filtersToDto,
  hasActiveFilters,
  formatManagerNames,
} from './defect-overview.models';

import './defect-overview.component.scss';

@Component({
  selector: 'app-defect-overview',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe],
  template: `
    <div class="screen screen-modal-container defect-overview">
      <!-- Busy indicator -->
      <div class="busy-indicator" *ngIf="isBusy">
        <div class="spinner"></div>
      </div>

      <!-- Close button -->
      <div><a class="screen-close" (click)="close()"></a></div>

      <!-- Filter panel -->
      <div class="defect-filter-panel">
        <div class="defect-filter-panel-toolbar">
          <div class="defect-filter-panel-toolbar-left">
            <!-- Sort dropdown -->
            <select [(ngModel)]="selectedSortIndex" (change)="applySorting()">
              <option [value]="-1" disabled selected>{{ 'defect_overview_sort_by_lbl' | translate }}</option>
              <option *ngFor="let opt of sortOptions; let i = index" [value]="i">{{ opt.title }}</option>
            </select>

            <!-- Filter toggle -->
            <a [class.open]="!hasActiveFilters()" (click)="showFilters = !showFilters" class="link-btn">
              <img src="assets/images/defect/tune-vertical.png" />
            </a>

            <!-- Clear filters -->
            <a class="link-btn" (click)="clearFilters()">
              <img src="assets/images/defect/close.png" />
            </a>

            <!-- Export button (Manager/Admin only) -->
            <a class="link-btn" (click)="exportDefects()" *ngIf="allowExport">
              {{ 'defect_overview_export_lbl' | translate }}
            </a>
          </div>

          <div class="defect-filter-panel-toolbar-right">
            <input
              type="search"
              placeholder="Název závady"
              [(ngModel)]="filters.search"
            />
            <a class="link-btn" (click)="applyFilters()">{{ 'defect_overview_search_lbl' | translate }}</a>
          </div>
        </div>

        <!-- Expandable filter panel -->
        <div class="defect-filter-panel-filters" *ngIf="showFilters">
          <div>
            <label>{{ 'defect_overview_filter_severity_lbl' | translate }}</label>
            <select [(ngModel)]="filters.severityLevelId" (change)="onSeverityFilterChanged()">
              <option [ngValue]="null" [disabled]="severityFilterDefaultDisabled">{{ severityFilterDefault }}</option>
              <option *ngFor="let s of filtersViewModel.severities" [ngValue]="s.id">{{ s.title }}</option>
            </select>
          </div>

          <div>
            <label>{{ 'defect_overview_filter_state_lbl' | translate }}</label>
            <select [(ngModel)]="filters.defectStateId" (change)="onStateFilterChanged()">
              <option [ngValue]="null" [disabled]="stateFilterDefaultDisabled">{{ stateFilterDefault }}</option>
              <option *ngFor="let s of filtersViewModel.states" [ngValue]="s.id">{{ s.title }}</option>
            </select>
          </div>

          <div>
            <label>{{ 'defect_overview_filter_created_from_lbl' | translate }}</label>
            <p class="input-group">
              <input type="date" [(ngModel)]="createdFromDate" class="form-control" />
            </p>
          </div>

          <div>
            <label>{{ 'defect_overview_filter_created_to_lbl' | translate }}</label>
            <p class="input-group">
              <input type="date" [(ngModel)]="createdToDate" class="form-control" />
            </p>
          </div>

          <div>
            <label>{{ 'defect_overview_filter_category1_lbl' | translate }}</label>
            <select [(ngModel)]="filters.category1Id" (change)="onCategory1FilterChanged()">
              <option [ngValue]="null" [disabled]="category1FilterDefaultDisabled">{{ category1FilterDefault }}</option>
              <option *ngFor="let c of filtersViewModel.categories1" [ngValue]="c.id">{{ c.title }}</option>
            </select>
          </div>

          <div>
            <label>{{ 'defect_overview_filter_category2_lbl' | translate }}</label>
            <select [(ngModel)]="filters.category2Id" (change)="onCategory2FilterChanged()">
              <option [ngValue]="null" [disabled]="category2FilterDefaultDisabled">{{ category2FilterDefault }}</option>
              <option *ngFor="let c of getFilteredCategories2()" [ngValue]="c.id">{{ c.title }}</option>
            </select>
          </div>

          <div>
            <label>{{ 'defect_overview_filter_category3_lbl' | translate }}</label>
            <select [(ngModel)]="filters.category3Id" (change)="onCategory3FilterChanged()">
              <option [ngValue]="null" [disabled]="category3FilterDefaultDisabled">{{ category3FilterDefault }}</option>
              <option *ngFor="let c of getFilteredCategories3()" [ngValue]="c.id">{{ c.title }}</option>
            </select>
          </div>

          <div>
            <label>{{ 'defect_overview_filter_manager_lbl' | translate }}</label>
            <select [(ngModel)]="filters.managerPersonalNum" (change)="onManagerFilterChanged()">
              <option [ngValue]="null" [disabled]="managerFilterDefaultDisabled">{{ managerFilterDefault }}</option>
              <option *ngFor="let m of filtersViewModel.managers" [ngValue]="m.personalNumber">{{ m.fullName }}</option>
            </select>
          </div>

          <div>
            <label>{{ 'defect_overview_filter_coordinator_lbl' | translate }}</label>
            <select [(ngModel)]="filters.coordinatorPersonalNum" (change)="onCoordinatorFilterChanged()">
              <option [ngValue]="null" [disabled]="coordinatorFilterDefaultDisabled">{{ coordinatorFilterDefault }}</option>
              <option *ngFor="let c of filtersViewModel.coordinators" [ngValue]="c.personalNumber">{{ c.fullName }}</option>
            </select>
          </div>
        </div>
      </div>

      <!-- Data table -->
      <div class="data-table defect-overview-table">
        <div *ngFor="let defect of defects" class="data-table-row">
          <!-- Severity mark with status icon -->
          <div class="mark-column data-table-row-item"
               [ngClass]="getSeverityClass(defect.severityLevelId)">
            <img [src]="defect.statusIcon" />
          </div>

          <!-- ID column -->
          <div class="data-table-row-item id-column">
            <div>ID: {{ defect.id }}</div>
          </div>

          <!-- Title and description -->
          <div class="data-table-row-item">
            <div class="content"><b>{{ defect.title }}</b></div>
            <div>{{ defect.description }}</div>
          </div>

          <!-- Severity, State, Date wrapper -->
          <div class="data-table-row-item wrapper">
            <div class="data-table-row inner">
              <div class="data-table-row-item">
                <div>{{ 'defect_overview_severity_lbl' | translate }}</div>
                <div class="content" [ngClass]="getSeverityTextClass(defect.severityLevelId)">
                  {{ defect.severity }}
                </div>
              </div>
              <div class="data-table-row-item">
                <div>{{ 'defect_overview_state_lbl' | translate }}</div>
                <div class="content">
                  <img [src]="defect.statusIconDark" height="16" width="16" />
                  <span [class.solved]="defect.stateId === 3">{{ defect.faultState }}</span>
                </div>
              </div>
              <div class="data-table-row-item">
                <div>{{ 'defect_overview_reported_lbl' | translate }}</div>
                <div class="content">{{ formatDate(defect.createdAt) }}</div>
              </div>
            </div>

            <!-- Expandable details -->
            <div class="data-table detail" *ngIf="expandedDefectId === defect.id">
              <div class="data-table-row">
                <div class="data-table-row-item">
                  <div>{{ 'defect_overview_cat1_lbl' | translate }}</div>
                  <div class="content">{{ defect.category1 }}</div>
                </div>
                <div class="data-table-row-item">
                  <div>{{ 'defect_overview_reporter_lbl' | translate }}</div>
                  <div class="content">{{ defect.createdBy }}</div>
                </div>
              </div>
              <div class="data-table-row">
                <div class="data-table-row-item">
                  <div>{{ 'defect_overview_cat2_lbl' | translate }}</div>
                  <div class="content">{{ defect.category2 }}</div>
                </div>
                <div class="data-table-row-item">
                  <div>{{ 'defect_overview_manager_lbl' | translate }}</div>
                  <div class="content">{{ defect.managersNames }}</div>
                </div>
              </div>
              <div class="data-table-row">
                <div class="data-table-row-item">
                  <div>{{ 'defect_overview_cat3_lbl' | translate }}</div>
                  <div class="content">{{ defect.category3 }}</div>
                </div>
                <div class="data-table-row-item">
                  <div>{{ 'defect_overview_coordinator_lbl' | translate }}</div>
                  <div class="content">{{ defect.coordinator?.fullName }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Expand arrow -->
          <div class="data-table-row-item row-detail-column" (click)="toggleExpand(defect.id)">
            <div class="arrow" [class.open]="expandedDefectId === defect.id"></div>
          </div>

          <!-- Go to location -->
          <div class="data-table-row-item goto-column" (click)="goToDefectLocation(defect)">
            <img src="assets/images/defect/map-marker-outline.png" />
          </div>

          <!-- Show detail -->
          <div class="data-table-row-item detail-column" (click)="showDetail(defect)">
            <img src="assets/images/defect/magnify-plus-outline.png" />
          </div>

          <!-- Edit -->
          <div class="data-table-row-item edit-column"
               [class.disabled]="!defect.editable"
               (click)="editDefect(defect)">
            <img *ngIf="defect.editable" src="assets/images/defect/pencil-outline.png" />
            <img *ngIf="!defect.editable" src="assets/images/defect/block_white.png" />
          </div>
        </div>
      </div>

      <!-- Legend -->
      <div class="defect-legend">
        <div>{{ 'defect_overview_legend_lbl' | translate }}</div>
        <div class="separator thick"></div>
        <div>
          <p class="header">{{ 'defect_overview_legend_status_lbl' | translate }}</p>
          <p></p>
        </div>
        <div class="separator red"></div>
        <div>
          <p class="header">{{ 'defect_overview_legend_state_critical_lbl' | translate }}</p>
          <p>{{ 'defect_overview_legend_state_critical_desc_lbl' | translate }}</p>
        </div>
        <div class="separator yellow"></div>
        <div>
          <p class="header">{{ 'defect_overview_legend_state_middle_lbl' | translate }}</p>
          <p>{{ 'defect_overview_legend_state_middle_desc_lbl' | translate }}</p>
        </div>
        <div class="separator blue"></div>
        <div>
          <p class="header">{{ 'defect_overview_legend_state_low_lbl' | translate }}</p>
          <p>{{ 'defect_overview_legend_state_low_desc_lbl' | translate }}</p>
        </div>
      </div>
    </div>
  `,
})
export class DefectOverviewComponent implements OnInit, OnDestroy {
  @Output() closeEvent = new EventEmitter<void>();
  @Output() viewDefect = new EventEmitter<IDefect>();
  @Output() editDefectEvent = new EventEmitter<IDefect>();
  @Output() goToLocation = new EventEmitter<IDefect>();

  private defectService = inject(DefectService);
  private codebookService = inject(CodebookService);
  private userRoleService = inject(DefectUserRoleService);
  private siteService = inject(SiteService);
  private translationService = inject(TranslationService);
  private cdr = inject(ChangeDetectorRef);

  // Source data
  private defectsSourceData: IDefect[] = [];

  // View model
  defects: DefectItemViewModel[] = [];
  filters: DefectFilters;
  filtersViewModel: FiltersViewModel = {
    severities: [],
    states: [],
    categories1: [],
    categories2: [],
    categories3: [],
    managers: [],
    coordinators: [],
  };

  // UI state
  isBusy = false;
  showFilters = false;
  expandedDefectId: number | null = null;
  allowExport = false;

  // Sort options
  sortOptions: SortDropdownOption[] = [];
  selectedSortIndex = -1;

  // Date filters
  createdFromDate: string | null = null;
  createdToDate: string | null = null;

  // Filter dropdown defaults
  private filterAllText = 'Vše';
  severityFilterDefault = '';
  severityFilterDefaultDisabled = true;
  stateFilterDefault = '';
  stateFilterDefaultDisabled = true;
  category1FilterDefault = '';
  category1FilterDefaultDisabled = true;
  category2FilterDefault = '';
  category2FilterDefaultDisabled = true;
  category3FilterDefault = '';
  category3FilterDefaultDisabled = true;
  managerFilterDefault = '';
  managerFilterDefaultDisabled = true;
  coordinatorFilterDefault = '';
  coordinatorFilterDefaultDisabled = true;

  private useDefaultFilter = true;
  private siteId = 0;
  private subscriptions: Subscription[] = [];

  constructor() {
    this.filters = createDefaultFilters(0);
  }

  ngOnInit(): void {
    // Get current site
    const site = this.siteService.selectedSite;
    this.siteId = site?.id ?? 1;
    this.filters.siteId = this.siteId;

    // Load user info first
    this.userRoleService.loadUserInfo().subscribe(() => {
      this.allowExport = this.userRoleService.canExport();
      this.initSortOptions();
      this.initFilterDefaults();
      this.loadData(true);
      this.cdr.detectChanges();
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  private initSortOptions(): void {
    this.sortOptions = [
      {
        id: SortOption.Severity,
        title: this.translationService.translate('defect_overview_severity_order_asc_lbl'),
        type: SortOrder.Asc,
      },
      {
        id: SortOption.Severity,
        title: this.translationService.translate('defect_overview_severity_order_desc_lbl'),
        type: SortOrder.Desc,
      },
      {
        id: SortOption.State,
        title: this.translationService.translate('defect_overview_state_order_asc_lbl'),
        type: SortOrder.Asc,
      },
      {
        id: SortOption.State,
        title: this.translationService.translate('defect_overview_state_order_desc_lbl'),
        type: SortOrder.Desc,
      },
      {
        id: SortOption.CreatedAt,
        title: this.translationService.translate('defect_overview_created_order_asc_lbl'),
        type: SortOrder.Asc,
      },
      {
        id: SortOption.CreatedAt,
        title: this.translationService.translate('defect_overview_created_order_desc_lbl'),
        type: SortOrder.Desc,
      },
    ];
  }

  private initFilterDefaults(): void {
    this.severityFilterDefault = this.translationService.translate('defect_overview_filter_severity_opt');
    this.stateFilterDefault = this.translationService.translate('defect_overview_filter_state_opt');
    this.category1FilterDefault = this.translationService.translate('defect_overview_filter_category1_opt');
    this.category2FilterDefault = this.translationService.translate('defect_overview_filter_category2_opt');
    this.category3FilterDefault = this.translationService.translate('defect_overview_filter_category3_opt');
    this.managerFilterDefault = this.translationService.translate('defect_overview_filter_manager_opt');
    this.coordinatorFilterDefault = this.translationService.translate('defect_overview_filter_coordinator_opt');
  }

  loadData(firstLoad = false): void {
    this.isBusy = true;

    // Load codebooks and defects in parallel
    forkJoin([
      this.codebookService.loadAllCodebooks(),
      this.codebookService.loadAllManagers(this.siteId),
      this.codebookService.loadAllCoordinators(this.siteId),
      this.defectService.filterDefects(this.siteId, filtersToDto(this.filters)),
    ]).subscribe({
      next: ([_, managers, coordinators, defects]) => {
        // Update filter view model
        this.filtersViewModel = {
          severities: this.codebookService.getSeverityLevels(),
          states: this.codebookService.getDefectStates(),
          categories1: this.codebookService.getCategories1(),
          categories2: this.codebookService.getCategories2(),
          categories3: this.codebookService.getCategories3(),
          managers,
          coordinators,
        };

        this.prepareViewModel(defects, firstLoad);
        this.isBusy = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('[DefectOverview] Error loading data:', error);
        this.isBusy = false;
        this.cdr.detectChanges();
      },
    });
  }

  private prepareViewModel(defects: IDefect[], firstLoad = false): void {
    this.defectsSourceData = defects;
    this.defects = [];

    if (defects.length === 0) {
      return;
    }

    const userPersonalNum = this.userRoleService.getUserPersonalNumber();

    // Apply default filter on first load (show user's own defects)
    if (this.useDefaultFilter && userPersonalNum) {
      const filteredItems = defects.filter(
        (p) =>
          (p.coordinator && p.coordinator.personalNumber === userPersonalNum) ||
          (p.managers && p.managers.some((m) => m.personalNumber === userPersonalNum))
      );

      if (filteredItems.length > 0) {
        defects = filteredItems;
      }

      if (defects.some((p) => p.coordinator && p.coordinator.personalNumber === userPersonalNum)) {
        this.filters.coordinatorPersonalNum = userPersonalNum;
      }

      if (defects.some((p) => p.managers && p.managers.some((m) => m.personalNumber === userPersonalNum))) {
        this.filters.managerPersonalNum = userPersonalNum;
      }
    }

    // Transform defects to view models
    defects.forEach((p) => {
      const defectState = this.codebookService.getDefectStateById(p.defectStateId);
      const severity = this.codebookService.getSeverityById(p.severityLevelId);
      const category1 = this.codebookService.getCategory1ById(p.category1Id);
      const category2 = this.codebookService.getCategory2ById(p.category2Id);
      const category3 = this.codebookService.getCategory3ById(p.category3Id);

      const isArchived = p.defectStateId === DefectStateEnum.Solved || p.defectStateId === DefectStateEnum.Refused;

      const defectVm: DefectItemViewModel = {
        id: p.id,
        title: p.title,
        description: p.description,
        faultState: defectState?.title ?? null,
        stateId: defectState?.id ?? 0,
        severity: severity?.title ?? null,
        severityLevelId: p.severityLevelId,
        category1: category1?.title ?? null,
        category2: category2?.title ?? null,
        category3: category3?.title ?? null,
        createdBy: p.createdBy,
        createdAt: new Date(p.createdAt * 1000),
        statusIcon: this.resolveStatusIconById(defectState?.id ?? 0, false),
        statusIconDark: this.resolveStatusIconById(defectState?.id ?? 0, true),
        managers: p.managers,
        coordinator: p.coordinator,
        managersNames: formatManagerNames(p.managers),
        isArchived,
        editable: this.userRoleService.canEditDefect(p) && !isArchived,
      };

      this.defects.push(defectVm);
    });
  }

  // Filter methods
  applyFilters(): void {
    this.isBusy = true;
    this.useDefaultFilter = false;

    // Convert date strings to timestamps
    if (this.createdFromDate) {
      this.filters.createdAtFrom = new Date(this.createdFromDate).getTime();
    } else {
      this.filters.createdAtFrom = null;
    }

    if (this.createdToDate) {
      this.filters.createdAtTo = new Date(this.createdToDate).getTime();
    } else {
      this.filters.createdAtTo = null;
    }

    this.defectService.filterDefects(this.siteId, filtersToDto(this.filters)).subscribe({
      next: (defects) => {
        this.prepareViewModel(defects);
        this.isBusy = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('[DefectOverview] Error applying filters:', error);
        this.isBusy = false;
        this.cdr.detectChanges();
      },
    });
  }

  applySorting(): void {
    if (this.selectedSortIndex < 0) {
      return;
    }

    const sortOption = this.sortOptions[this.selectedSortIndex];
    this.filters.sortInfo = [
      {
        sortOption: sortOption.id,
        sortOrder: sortOption.type,
      },
    ];

    this.applyFilters();
  }

  clearFilters(): void {
    this.useDefaultFilter = false;
    this.resetFilters();
    this.loadData();
  }

  private resetFilters(): void {
    this.selectedSortIndex = -1;
    this.createdFromDate = null;
    this.createdToDate = null;
    this.filters = createDefaultFilters(this.siteId);
  }

  exportDefects(): void {
    this.defectService.exportDefects(this.siteId, filtersToDto(this.filters)).subscribe({
      next: ({ blob, filename }) => {
        this.defectService.downloadFile(blob, filename);
      },
      error: (error) => {
        console.error('[DefectOverview] Error exporting defects:', error);
      },
    });
  }

  // Filter change handlers
  onSeverityFilterChanged(): void {
    this.severityFilterDefault = this.filterAllText;
    this.severityFilterDefaultDisabled = false;
  }

  onStateFilterChanged(): void {
    this.stateFilterDefault = this.filterAllText;
    this.stateFilterDefaultDisabled = false;
  }

  onCategory1FilterChanged(): void {
    this.category1FilterDefault = this.filterAllText;
    this.category1FilterDefaultDisabled = false;
    // Reset dependent categories
    this.filters.category2Id = null;
    this.filters.category3Id = null;
  }

  onCategory2FilterChanged(): void {
    this.category2FilterDefault = this.filterAllText;
    this.category2FilterDefaultDisabled = false;
    // Reset dependent category
    this.filters.category3Id = null;
  }

  onCategory3FilterChanged(): void {
    this.category3FilterDefault = this.filterAllText;
    this.category3FilterDefaultDisabled = false;
  }

  onManagerFilterChanged(): void {
    this.managerFilterDefault = this.filterAllText;
    this.managerFilterDefaultDisabled = false;
  }

  onCoordinatorFilterChanged(): void {
    this.coordinatorFilterDefault = this.filterAllText;
    this.coordinatorFilterDefaultDisabled = false;
  }

  // Category filtering
  getFilteredCategories2() {
    if (this.filters.category1Id === null) {
      return this.filtersViewModel.categories2;
    }
    return this.filtersViewModel.categories2.filter((c) => c.category1Id === this.filters.category1Id);
  }

  getFilteredCategories3() {
    if (this.filters.category2Id === null) {
      return this.filtersViewModel.categories3;
    }
    return this.filtersViewModel.categories3.filter((c) => c.category2Id === this.filters.category2Id);
  }

  // UI helpers
  hasActiveFilters(): boolean {
    return hasActiveFilters(this.filters);
  }

  toggleExpand(defectId: number): void {
    this.expandedDefectId = this.expandedDefectId === defectId ? null : defectId;
  }

  getSeverityClass(severityLevelId: number): string {
    switch (severityLevelId) {
      case 1:
        return 'severity-heigh';
      case 2:
        return 'severity-medium';
      case 3:
        return 'severity-low';
      default:
        return '';
    }
  }

  getSeverityTextClass(severityLevelId: number): string {
    switch (severityLevelId) {
      case 1:
        return 'severity-heigh-text';
      case 2:
        return 'severity-medium-text';
      case 3:
        return 'severity-low-text';
      default:
        return '';
    }
  }

  formatDate(date: Date): string {
    if (!date) {
      return '';
    }
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const seconds = String(d.getSeconds()).padStart(2, '0');
    return `${day}.${month}.${year} ${hours}:${minutes}:${seconds}`;
  }

  private resolveStatusIconById(id: number, dark = false): string {
    const color = dark ? 'black' : 'white';
    const solvedColor = dark ? 'green' : 'white';

    switch (id) {
      case 1:
        return `assets/images/defect/reported-${color}.png`;
      case 2:
        return `assets/images/defect/inprogress-${color}.png`;
      case 3:
        return `assets/images/defect/solved-${solvedColor}.png`;
      case 4:
        return `assets/images/defect/planned-${color}.png`;
      default:
        return `assets/images/defect/reported-${color}.png`;
    }
  }

  // Actions
  close(): void {
    this.closeEvent.emit();
  }

  showDetail(defectVm: DefectItemViewModel): void {
    const defect = this.defectsSourceData.find((p) => p.id === defectVm.id);
    if (defect) {
      this.viewDefect.emit(defect);
    }
  }

  goToDefectLocation(defectVm: DefectItemViewModel): void {
    const defect = this.defectsSourceData.find((p) => p.id === defectVm.id);
    if (defect) {
      this.goToLocation.emit(defect);
      this.close();
    }
  }

  editDefect(defectVm: DefectItemViewModel): void {
    if (!defectVm.editable || defectVm.isArchived) {
      return;
    }

    const defect = this.defectsSourceData.find((p) => p.id === defectVm.id);
    if (defect) {
      this.editDefectEvent.emit(defect);
    }
  }
}
